import api from './api';

// Types
export interface OrderItem {
    id: string;
    product_id: string;
    quantity: number;
    price: string;
    product?: {
        name: string;
        image_url?: string;
    };
}

export interface Order {
    id: string;
    user_id: string;
    user_phone_number?: string;
    items: OrderItem[];
    total_amount: string;
    gold_price_at_time: string;
    status: OrderStatus;
    created_at: string;
}

export type OrderStatus =
    | 'pending'
    | 'confirmed'
    | 'processing'
    | 'shipped'
    | 'delivered'
    | 'cancelled';

export interface CreateOrderItem {
    product_id: string;
    quantity: number;
}

export interface CreateOrderRequest {
    items: CreateOrderItem[];
}

export interface OrderListResponse {
    orders: Order[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
}

// Order status labels and colors
export const orderStatusConfig: Record<OrderStatus, { label: string; color: string; bgColor: string }> = {
    pending: { label: 'در انتظار تایید', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
    confirmed: { label: 'تایید شده', color: 'text-blue-700', bgColor: 'bg-blue-100' },
    processing: { label: 'در حال پردازش', color: 'text-purple-700', bgColor: 'bg-purple-100' },
    shipped: { label: 'ارسال شده', color: 'text-indigo-700', bgColor: 'bg-indigo-100' },
    delivered: { label: 'تحویل داده شده', color: 'text-green-700', bgColor: 'bg-green-100' },
    cancelled: { label: 'لغو شده', color: 'text-red-700', bgColor: 'bg-red-100' },
};

// Order Service
export const orderService = {
    /**
     * دریافت لیست سفارشات کاربر
     */
    getOrders: async (page = 1, pageSize = 10): Promise<OrderListResponse> => {
        const response = await api.get<OrderListResponse>(`/orders?page=${page}&page_size=${pageSize}`);
        return response.data;
    },

    /**
     * دریافت جزئیات یک سفارش
     */
    getOrder: async (id: string): Promise<Order> => {
        const response = await api.get<Order>(`/orders/${id}`);
        return response.data;
    },

    /**
     * ایجاد سفارش جدید
     */
    createOrder: async (items: CreateOrderItem[]): Promise<Order> => {
        const response = await api.post<Order>('/orders', { items });
        return response.data;
    },

    /**
     * دریافت تمام سفارشات (پنل ادمین)
     */
    getAllOrders: async (page = 1, pageSize = 10, status?: OrderStatus): Promise<OrderListResponse> => {
        let url = `/admin/orders?page=${page}&page_size=${pageSize}`;
        if (status) {
            url += `&status=${status}`;
        }
        const response = await api.get<OrderListResponse>(url);
        return response.data;
    },

    /**
     * تغییر وضعیت سفارش (پنل ادمین)
     */
    updateStatus: async (id: string, status: OrderStatus): Promise<Order> => {
        const response = await api.patch<Order>(`/admin/orders/${id}/status`, { status });
        return response.data;
    },

    /**
     * تولید تصویر فاکتور سفارش
     */
    generateShareImage: async (id: string): Promise<{ image_url: string }> => {
        const response = await api.get<{ image_url: string }>(`/orders/${id}/share`);
        return response.data;
    },

    /**
     * خروجی اکسل (CSV) سفارشات
     */
    exportOrders: async (status?: OrderStatus): Promise<Blob> => {
        let url = '/admin/orders/export';
        if (status) {
            url += `?status=${status}`;
        }
        const response = await api.get(url, { responseType: 'blob' });
        return response.data;
    },
};

export default orderService;
